import stores from 'cd-eft/models/Stores';
import { DynamicTerraceHub } from './DynamicTerraceHub';
import { DynamicTableStoreBase } from '../DynamicTableStoreBase';
import { ProfileKeyCode } from '../DynamicProfileEnums';
import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import { ProfileController } from 'cd-eft/services/eftSetApi';
import { ITerraceVertexParamsDTO } from 'cd-eft/models/Stores/TerraceDesignStore';
import { parseStationString, formatStationString } from 'cd-eft/utils/stationUtilities';

const { designStore, terraceDesignStore } = stores.AppStore
class DynamicTerraceTableStore extends DynamicTableStoreBase {
  protected hub: DynamicTerraceHub;
  private _initialTerraceDesignStation;

  protected _highlitedRows: string[] = [];

  /**
   *
   */
  constructor(hub: DynamicTerraceHub) {
    super();

    this.hub = hub;
    this.profileKeys = [ProfileKeyCode.TGS, ProfileKeyCode.REACH];
  }

  // adapt to global stores
  set setStore(setStore) {
    this.hub.setStore = setStore;
  }

  get setStore() {
    return this.hub.setStore;
  }

  public set terraceStore(store) {
    this.hub.terraceStore = store;
  }

  public get terraceStore() {
    return this.hub.terraceStore;
  }

  private get initialTerraceDesignStation() {
    if (!this._initialTerraceDesignStation) {
      this._initialTerraceDesignStation = terraceDesignStore.initialTerraceDesignStation;
    }
    return this._initialTerraceDesignStation;
  }

  public highlightRows(i: number) {
    const id = `row-terraceDesignStation-${i}`;
    if (!this._highlitedRows.some(r => r === id)) {
      this.tryHighlight(id);
      this._highlitedRows.push(id);
    }
  }

  public clearHighlights() {
    for (const row of this._highlitedRows) {
      this.tryHighlight(row, false);
    }
    this._highlitedRows = [];
  }

  protected tryHighlight(id: string, highlight: boolean = true) {
    const element = document.getElementById(id);
    if (element) {
      if (highlight) {
        element.classList.add('highlighted-row');
      } else {
        element.classList.remove('highlighted-row');
      }
    }
  }

  /** abstract method implementation */
  protected reset() {
    if (this.currentObject !== terraceDesignStore.terraceDesignObject) this.currentObject = terraceDesignStore.terraceDesignObject;
    if (this.currentObject) {
      this.currentObject.terraceDesignStations.length = 0;
    }
  }

  protected insertRow(newVertex: any, vertexIndex: any) { }

  protected addRow(vertex: XyzPoint, state: string, vertexIndex: number) { }

  protected calculateStationDistances(indexToDelete?: number) { }

  public async addRows(channelData: any) {
    designStore.setLoader('Loading profile reaches...');

    const stations = [{ ...this.initialTerraceDesignStation }];
    channelData.forEach((point, idx) => {
      const newRow = { ...this.initialTerraceDesignStation, ...point };
      // Format numeric values
      if (newRow.channelElev !== null && newRow.channelElev !== undefined) {
        newRow.channelElev = parseFloat(newRow.channelElev).toFixed(1);
        newRow.cutFill = parseFloat((newRow.channelElev - newRow.groundElev).toFixed(1));
      }
      if (point.reachId !== 'START' && point.reachId !== 'END' && point.reachId !== 'R-0' && !point.status) {
        if (!newRow.status) {
          newRow.status = {};
        }
        newRow.status = 'IN PROGRESS';
      } else {
        newRow.status = point.status
      }
      stations.push(newRow);
    });
    stations.splice(0, 1);
    terraceDesignStore.terraceDesignObject.terraceDesignStations = stations;

    await this.hub.ProfileStore?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);

    // Clone object after digitizing reaches
    terraceDesignStore.terraceGroupsOriginals = [];
    terraceDesignStore.terraceGroupsOriginals.push(
      {
        groupCount: 0,
        currentObject: JSON.parse(JSON.stringify(terraceDesignStore.terraceDesignObject))
      }
    );
    designStore.setLoader('');
  }


  protected async insertRows(dataPoint: XyzPoint, vertexIndex: number, keyCode: ProfileKeyCode, addTerraceEdits?: any) {
    const stations1 = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    const stations = JSON.parse(JSON.stringify(stations1));
    const originalStations = JSON.parse(JSON.stringify(stations));

    // Find the index of 'R-0' in the stations array
    const r0Index = stations.findIndex(station => station.reachId === 'R-0');

    // Adjust the vertexIndex by the index of 'R-0'
    vertexIndex += r0Index;
    stations[vertexIndex].status = "IN PROGRESS";
    if (vertexIndex < 0 || vertexIndex >= stations.length) {
      return;
    }

    const formattedStationX = formatStationString(dataPoint.getX().toFixed(1));
    const formattedStationY = dataPoint.getY().toFixed(1);

    // Calculate groundZ for current reach
    const payload = {
      alignedElementDesignParametersId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
      reachStationDistance: parseFloat(dataPoint.getX().toFixed(1)) // new station x value
    };

    const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);

    let groundZ = 0;
    let cutFill = 0;

    // cutFill: channelZ - groundZ
    if (response.data && response.data.groundElevation !== undefined) {
      groundZ = parseFloat(response.data.groundElevation.toFixed(1));
      cutFill = parseFloat((parseFloat(formattedStationY) - groundZ).toFixed(1));
    }

    const newRow = {
      ...stations[vertexIndex - 1],
      endStation: formattedStationX,
      channelElev: parseFloat(formattedStationY),
      groundElev: groundZ,
      cutFill: cutFill,
      status: 'IN PROGRESS',
      reachId: `R-${vertexIndex - r0Index}`,
      startStation: stations[vertexIndex - 1].endStation,
      templateParams: {
        ...stations[vertexIndex - 1].templateParams
      },
      drainStation: false,
      blockStation: false,
      drainStationDto: {
        drainName: null,
        drainFraction: 0,
        drainOffset: 0,
        ugoAndInlet: null,
        location: {
          id: 0,
          pointName: null,
          description: null,
          tinnable: true,
          visible: true,
          wkid: 0,
          x: 0,
          y: 0,
          z: 0
        }
      },
      blockStationDto: {
        sideSlopeMode: 0,
        sideSlope: 0,
        limits: 0,
        topWidth: 0,
        isSplitSubbasin: false,
        blockHeight: null,
        location: {
          id: 0,
          pointName: null,
          description: null,
          tinnable: true,
          visible: true,
          wkid: 0,
          x: 0,
          y: 0,
          z: 0
        }
      }

    };

    // Calculate stationLength and grade for the new row
    const prevStation = stations[vertexIndex - 1];
    if (prevStation.reachId !== "START") {
      const deltaCurY = (prevStation.channelElev) - newRow.channelElev;
      const deltaCurX = parseStationString(prevStation.endStation) - parseStationString(newRow.endStation);
      if (deltaCurX !== 0) {
        newRow.grade = deltaCurY / deltaCurX;
        newRow.stationDist = Math.abs(deltaCurX);
      }
    }

    const stationsInsertedAndUpdated: any = [];
    stations.splice(vertexIndex, 0, newRow);
    stationsInsertedAndUpdated.push(newRow);

    // Update reachId, startStation, groundZ, cutFill, stationLength, and grade for the rows after the inserted row
    for (let index = vertexIndex + 1; index < stations.length; index++) {
      if (stations[index].reachId !== "END" && stations[index].reachId !== "START" && stations[index].reachId !== "R-0") {
        // Update Status
        stations[index].status = 'IN PROGRESS'
        const reachIdNumber = parseInt(stations[index].reachId.split('-')[1]);
        stations[index].reachId = `R-${reachIdNumber + 1}`;
        stations[index].startStation = stations[index - 1].endStation;

        if (index === vertexIndex + 1 || index === stations.length - 1) {
          // Recalculate groundZ and cutFill
          const payload = {
            alignedElementDesignParametersId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
            reachStationDistance: parseFloat(stations[index].endStation) // station x value of the current row
          };

          const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);

          if (response.data && response.data.groundElevation !== undefined) {
            stations[index].groundElev = parseFloat(response.data.groundElevation.toFixed(1));
            stations[index].cutFill = parseFloat((stations[index].channelElev - stations[index].groundElev).toFixed(1));
          }

          // Recalculate stationLength and grade
          const prevStation = stations[index - 1];
          if (prevStation.reachId !== "START") {
            const deltaCurY = prevStation.channelElev - stations[index].channelElev;
            const deltaCurX = parseStationString(prevStation.endStation) - parseStationString(stations[index].endStation);
            if (deltaCurX !== 0) {
              stations[index].grade = deltaCurY / deltaCurX;
              stations[index].stationDist = Math.abs(deltaCurX);
            }
          }
        }
      }
      // Only push the station into the array if its reachId is not "END"
      if (stations[index].reachId !== "END") {
        stationsInsertedAndUpdated.push(stations[index]); // Push each updated station into the array
      }
    }
    const allUpdatedValues = new Map();

    // Compare original and updated stations to find the stations that were added or modified
    stationsInsertedAndUpdated.forEach((updatedStation) => {
      const originalStation = originalStations.find((os) => {
        if (updatedStation.originalReachId) {
          return os.reachId === updatedStation.originalReachId;
        }
        return os.endStation === updatedStation.endStation;
      });

      if (!originalStation) {
        // A newly added station
        allUpdatedValues.set("Add", updatedStation);
      } else {
        const isChanged =
          originalStation.endStation !== updatedStation.endStation ||
          originalStation.reachId !== updatedStation.reachId ||
          parseFloat(originalStation.channelElev) !== parseFloat(updatedStation.channelElev);

        if (isChanged) {
          allUpdatedValues.set(originalStation.reachId, updatedStation);
        }
      }
    });
    terraceDesignStore.terraceDesignObject.terraceDesignStations = stations;
    await addTerraceEdits(allUpdatedValues, false, true);

    //Send all the updated values to PEM at once
    await this.hub.ProfileStore?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId)
  }

  public async modifyRows(dataPoint: XyzPoint, vertexIndex: number, addTerraceEdits?: any): Promise<Map<any, any> | void> {
    const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;

    if (!stations || stations.length < 1) {
      return;
    }
    const originalStations = JSON.parse(JSON.stringify(stations));

    //update table values based on changed station values
    await this.hub.ProfileStore?.updateTableOnChangeStationValue(dataPoint, vertexIndex);

    // Prepare the updated values
    const updatedStations = [stations[vertexIndex]];
    if (vertexIndex + 1 < stations.length && stations[vertexIndex + 1].reachId !== 'END') {
      updatedStations.push(stations[vertexIndex + 1]);
    }

    // Create a map of updated values
    const allUpdatedValues = new Map();
    updatedStations.forEach((updatedStation) => {
      const originalStation = originalStations.find(
        (os) => os.reachId === updatedStation.reachId
      );
      if (originalStation) {
        // Check if the station has been updated
        if (
          originalStation.endStation !== updatedStation.endStation ||
          originalStation.channelElev !== updatedStation.channelElev ||
          originalStation.grade !== updatedStation.grade ||
          originalStation.stationDist !== updatedStation.stationDist ||
          originalStation.groundElev !== updatedStation.groundElev ||
          originalStation.cutFill !== updatedStation.cutFill
        ) {
          allUpdatedValues.set(updatedStation.reachId, updatedStation);
        }
      } else {
        // New station added
        allUpdatedValues.set(updatedStation.reachId, updatedStation);
      }
    });

    terraceDesignStore.terraceDesignObject.terraceDesignStations = stations;
    await addTerraceEdits(allUpdatedValues, false, true);
    await this.hub.ProfileStore?.redrawMappingProfiles();
    await this.hub.ProfileStore?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
  }

  public async calculateNewStartOrEndReach(index, stations, calculateGrade = true) {
    const station: ITerraceVertexParamsDTO[] = stations[index];

    let stationDistance: string;
    let adjacentStation;
    let length: number;
    if ((index === 0 && terraceDesignStore.terraceDesignObject.terraceDesignStations[0].stationDist > 0) || (index === stations.length - 1 && terraceDesignStore.terraceDesignObject.terraceDesignStations[terraceDesignStore.terraceDesignObject.terraceDesignStations.length - 1].stationDist > 0)) {
      if (index === 0) {
        adjacentStation = stations[1];
        const formattedStation = parseFloat(adjacentStation.station.replace("+", ""));
        length = Number(terraceDesignStore.terraceDesignObject.terraceDesignStations[0].stationDist);
        stationDistance = (Number(formattedStation) - length).toFixed(1);
        station[0].endStation = adjacentStation.station;
        station[0].startStation = this.formatTerraceStation(stationDistance);
      } else {
        adjacentStation = stations[stations.length - 2];
        const formattedStation = (parseFloat(adjacentStation.station.replace("+", "")));
        length = Number(terraceDesignStore.terraceDesignObject.terraceDesignStations[terraceDesignStore.terraceDesignObject.terraceDesignStations.length - 1].stationDist);
        stationDistance = Number((formattedStation + length)).toFixed(1);
        station[index].startStation = adjacentStation.station;
        station[index].endStation = this.formatTerraceStation(stationDistance);
      }

      const payload = {
        alignedElementDesignParametersId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
        reachStationDistance: parseFloat(stationDistance)
      };
      const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);
      if (response.data && response.data.groundElevation !== undefined) {
        station[index].groundElev = parseFloat(response.data.groundElevation.toFixed(1));
        station[index].channelElev = station[index].groundElev;
      }

      station[index].stationDist = parseFloat(length.toFixed(1));
      if (!terraceDesignStore.gradeEdited &&
        !terraceDesignStore.stationLengthEdited &&
        !terraceDesignStore.channelStartGradeLengthChangedGlobal &&
        !terraceDesignStore.channelEndGradeLengthChangedGlobal) {
        station[index].grade = parseFloat(((adjacentStation.channelZ - station[index].groundElev) / length).toFixed(2));
      }
    } else {
      station[index].endStation = '';
      station[index].startStation = '';
      station[index].groundElev = 0.0;
      station[index].channelElev = 0.0;
      station[index].stationDist = 0.0;
      station[index].grade = 0;
    }
    return station;
  }

  public formatTerraceStation(input: string): string {
    if (!isNaN(parseFloat(input)) && isFinite(parseFloat(input))) {

      // Split the string into integer and decimal parts
      const [integerPart, decimalPart] = input.split('.');
      const quotient: number = Math.floor(parseInt(integerPart) / 100);
      const remainder: number = parseInt(integerPart) % 100;

      // Combine the quotient and remainder
      // const result: string = quotient.toString() + '+' + ((remainder >= 0 && remainder < 10) ? ('0' + remainder.toString()) : remainder.toString());
      const twoDigits = (remainder >= 0 && remainder < 10) ? ('0' + remainder.toString()) : remainder.toString();
      const result: string = decimalPart ? `${quotient}+${twoDigits}.${decimalPart}` : `${quotient}+${twoDigits}`;
      return result;
    }
    return '';
  }
}

export { DynamicTerraceTableStore };
